//listen for applicable events and decide whether user should be prompted to rate the app.
import {getJsonItem, setItem} from "../device/localStorage";
import nativeBridge from "../nativeBridge";
import {subscribeToReceiveMessagesFromNativeApp} from "../eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {IAppRatingResult} from "../eventBus/nativeApp/FromNativeAppEventTypes";
import {subscribeToScorerEvents} from "../eventBus/scorer/scorerEventBus";
import {ScorerEventTypes} from "../eventBus/scorer/ScorerEventTypes";
import {IPerformanceScore} from "../../models/scorer/IPerformanceScore";
import deviceInfo from "../deviceInfo/deviceInfo";
import {isAppEnvDev} from "../../config/environment";
import {logEventPromptUserToRateApp} from "../log/events/native-prompt-rate-app";
import {allowAppRateByTypeConfig, appRatingPromptEnabled} from "../../config/rating";

const appRatingLocalStorageKey = `appRatingData`;

const firstPromptAfterNScores = 10;
const secondPromptAfterNScores = 30;
const thirdPromptAfterNScores = 60;
const minScoreForPrompt = 100;
const maxNumberOfTimesToPromptTheUser = 3;

interface IAppRatingData{
  numberOfTimesUserHasBeenPromptedForRating: number,
  numberOfScoresReceivedSinceLastPromptToRate: number,
  dontPromptTheUserAgain: boolean;
}

/**
 * default data for AppRatingData
 * used when AppRatingData doesn't exist
 * or to reset AppRatingData in dev
 */
const defaultAppRatingData: IAppRatingData = {
  numberOfTimesUserHasBeenPromptedForRating: 0,
  numberOfScoresReceivedSinceLastPromptToRate: 0,
  dontPromptTheUserAgain: false
};

class AppRating{
  appRatingData: IAppRatingData;

  constructor() {
    // is prompting users to rate app enabled
    if (!appRatingPromptEnabled){
      // app rating prompt disabled, return to exit
      return;
    }

    // is rating allowed by device
    const isRatingAllowed = AppRating.isRatingAllowedForDevice();
    if (!isRatingAllowed){
      // rating not allowed for current device, return to stop setup
      return;
    }
    // if not running in native wrapper
    if(!nativeBridge.isRunningInNativeContainer){ return; }

    this.appRatingData = getAppRatingData();

    if(this.appRatingData.dontPromptTheUserAgain){ return; }

    subscribeToReceiveMessagesFromNativeApp((m)=>{
      switch(m.type){
        case "AppRatingResult":
          this.handleAppRatingResultFromNative(m);
          break;
      }
    });
    subscribeToScorerEvents((m: ScorerEventTypes) => {
      switch(m.type){
        case "performanceScoringCompleted":
          this.handlePerformanceScoreCompleted(m.performanceScore);
      }
    });
  }

  private static isRatingAllowedForDevice(): boolean {
    // is device mobile (iPhone)
    if (deviceInfo.getIsMobile()){
      // return if mobile (iPhone) is allowed to rate app
      return allowAppRateByTypeConfig.iPhone;
    }
    // is device iPad
    if (deviceInfo.getIsIPad()){
      // return if iPad is allowed to rate app
      return allowAppRateByTypeConfig.iPad;
    }
    // is device desktop
    if (deviceInfo.getIsDesktop()){
      // return if desktop is allowed to rate app
      return allowAppRateByTypeConfig.desktop;
    }

    // unknown device, not able to rate
    return false;
  }

  /**
   * appRatingResult.wasSuccessful only means that the prompt was successfully shown to the user.
   * There is no way of knowing whether the user actually rated, or said not now.
   * @param appRatingResult
   * @private
   */
  private handleAppRatingResultFromNative(appRatingResult: IAppRatingResult){
    this.appRatingData.numberOfTimesUserHasBeenPromptedForRating += 1;
    this.appRatingData.numberOfScoresReceivedSinceLastPromptToRate = 0;
    if(this.appRatingData.numberOfScoresReceivedSinceLastPromptToRate > maxNumberOfTimesToPromptTheUser){
      this.appRatingData.dontPromptTheUserAgain = true;
    }
    saveAppRatingData(this.appRatingData);
  }

  private handlePerformanceScoreCompleted(performanceScore: IPerformanceScore) {
    //increment and save
    this.appRatingData.numberOfScoresReceivedSinceLastPromptToRate += 1;
    saveAppRatingData(this.appRatingData);

    //determine if the user has received the min number of scores to be considered for prompt
    const {numberOfScoresReceivedSinceLastPromptToRate, numberOfTimesUserHasBeenPromptedForRating} = this.appRatingData;
    const minNumberOfScoresNeededForNextPrompt = getMinNumberOfScoresNeededForNextPrompt(numberOfTimesUserHasBeenPromptedForRating);
    const hasMinNumberOfScoresToBeConsideredForPrompting = numberOfScoresReceivedSinceLastPromptToRate >= minNumberOfScoresNeededForNextPrompt;
    const hasMinScoreToBeConsideredForPrompting = performanceScore.roundedScorePercentage >= minScoreForPrompt;

    // can user be prompted again without hitting max times
    const isUserAbleToBePromptedAgain: boolean = numberOfTimesUserHasBeenPromptedForRating < maxNumberOfTimesToPromptTheUser;

    // should user be presented with prompt to rate app
    const shouldPromptUserToRateApp: boolean = (
      hasMinNumberOfScoresToBeConsideredForPrompting
      && hasMinScoreToBeConsideredForPrompting
      && isUserAbleToBePromptedAgain
    );

    if (!shouldPromptUserToRateApp) {
      // not able to prompt user this time, return to exit
      return;
    }

    if (isAppEnvDev) {
      // is dev, let user know we would prompt user to rate app
      alert(`Prompt User to Rate App`);
      // reset app rating data to default (due to being in dev)
      this.appRatingData = {...defaultAppRatingData};
      saveAppRatingData(this.appRatingData);
    }
    // log event that we will prompt user to rate app
    void logEventPromptUserToRateApp();
    // send rating prompt to native
    nativeBridge.tellNativeToPromptTheUserToRateTheApp();

  }
}

function getMinNumberOfScoresNeededForNextPrompt(numberOfTimesUserHasBeenPromptedForRating: number){
  switch (numberOfTimesUserHasBeenPromptedForRating) {
    case 0: return firstPromptAfterNScores;
    case 1: return secondPromptAfterNScores;
    case 2: return thirdPromptAfterNScores;
    default: return Number.MAX_VALUE;
  }
}

function saveAppRatingData(appRatingData: IAppRatingData){
  setItem(appRatingLocalStorageKey, appRatingData);
}
/**
 * Retrieve app rating data from local storage.  If not present in local storage
 */
function getAppRatingData(){
  let appRatingData = getJsonItem<IAppRatingData>(appRatingLocalStorageKey);
  if(!appRatingData){
    appRatingData = {...defaultAppRatingData};
    setItem(appRatingLocalStorageKey, appRatingData);
  }
  return appRatingData;
}

const appRating = new AppRating();
export default appRating;
